import { createSignal, For, JSXElement, onMount, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { Checkbox } from '../../ui-components/inputs/checkbox/checkbox';
import { Link } from '../../ui-components/link/link';
import { Text } from '../../ui-components/text/text';
import { StyledHorizontalSpace, StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { StyledLoadingSpinner } from '../loading-spinner/loading-spinner.styles';
import { StyledErrorMessage } from '../login-form/login-form-styles';
import { FormField, FormStoreKey } from './form-types';
import {
    StyledRegistrationForm,
    StyledFormInput,
    StyledFormSelect,
    StyledCheckboxGroup,
    StyledFormSelectWrapper,
    StyledIconWrapper,
    StyledPatientInfo,
    StyledFormSelectLabel
} from './registration-form-styles';
import { validateCaptcha } from '../../tools/validate-captcha';
import { AngleDownIcon } from '../icons-library/angle-down';
import { validate } from './validation';
import { getCountryOptions, getPlaceholder, getUserTypeOptions } from './helpers';
import { formStore, resetFormStore, setError, setFormStoreField } from './registration-form-store';
import { useLocation } from '@solidjs/router';
import { logIn } from '../../tools/login-user';
import { StyledFlexColumn, StyledFlexRow } from '../../ui-components/utility-style-components/flex';
import { atosGlobalCountrySlugToCountryCode } from '../../tools/countries-data';
import { ErrorCatcher } from '../../tools/error-catcher';

type RegistrationFormProps = {
    createUser: (captchaToken: string, arrivedFromUrl?: string) => Promise<UserServiceResponse>;
    fields: FormField[];
    successMessageComponents: JSXElement;
    navigationComponents?: JSXElement;
    successIcon?: JSXElement;
    isAtosCareSite: boolean;
};

export type UserServiceResponse = {
    [key: string]: {
        status: string;
        message: string;
    };
};


export const RegistrationForm = (props: RegistrationFormProps) => {
    const {
        localize,
        privacyPolicyPage,
        recaptchaSiteKey,
        siteInfo,
        updateUser,
        translations,
        coursesListingPage,
        deploymentEnvironment,
        ssoBaseUrl,
    } = useContext(AppContext);
    const [success, setSuccess] = createSignal(false);
    const [arrivedFromUrl, setArrivedFromUrl] = createSignal('');
    const [loading,setLoading] = createSignal(false);
    
    const location = useLocation() as { state?: { fromUrl?: string } };

    onMount(() => {
        if (location.state?.fromUrl) {
            setArrivedFromUrl(location.state.fromUrl);
            delete location.state.fromUrl;   
        }

        if (!document.querySelector('#grecaptcha')) {
            const scriptTag = document.createElement('script');
            scriptTag.type = 'text/javascript';
            scriptTag.id = 'grecaptcha';
            scriptTag.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
            document.head.appendChild(scriptTag);
        }
        
        // Do not set a default country for atos-care
        const currentSelectCountryCode = siteInfo.siteType === 'atos-care' 
            ? undefined
            : atosGlobalCountrySlugToCountryCode[ siteInfo.siteSlug as keyof typeof atosGlobalCountrySlugToCountryCode ];

        if (currentSelectCountryCode) {
            setFormStoreField({ country: currentSelectCountryCode });
        }
    });

    const isBusinessLead = () => formStore.fields[ 'user-type' ] === 'HCP' || formStore.fields[ 'user-type' ] === 'Other';

    const submit = async (e: any) => {
        e.preventDefault();

        const extraFields = [];

        if (isBusinessLead()) {
            extraFields.push({
                name: 'Company',
                slug: 'company',
                required: true,
            });
        }
        const validForm = validate({ fields: [...props.fields, ...extraFields], localize });

        if (!validForm) return;

        setLoading(true);

        try {            
            const captchaToken = await validateCaptcha('register_user', recaptchaSiteKey);

            if (!captchaToken) throw new Error('Captcha validation failed - probably a bot.');

            const response: UserServiceResponse = await props.createUser(captchaToken, arrivedFromUrl());
            
            const { status, message } = siteInfo.siteType === 'atos-care' ? response.createCareUser : response.createUser;
            if (status !== 'OK') throw new Error(message);

            setLoading(false);
            setSuccess(true);

            if (siteInfo.siteType === 'atos-care') {
                await handleLogin();
            }

            resetFormStore();
        } catch (e: any) {
            setLoading(false);
            console.log('Error submitting form: ', e);
                    
            if (e.message === 'USRE') {
                setError(localize('email-already-in-use', 'A user with that email already exists'));
            } else if (e.message === 'invalid-showpad-code') {
                setError(localize('invalid-showpad-code-registration', 'Invalid e-learning code provided - try again or leave empty in order to continue'));
            } else {
                setError(localize('form-submit-error', 'Error submitting the form, please try again later'));
            }
        }
    };

    const handleLogin = async () => {
        const args = {
            email: formStore.fields.email,
            password: formStore.fields.password,
        };

        const { success, message } = await logIn(args, updateUser, recaptchaSiteKey, deploymentEnvironment,  ssoBaseUrl);

        if (!success) {
            if (message === 'invalid-credentials') {
                setError(localize('invalid-credentials', 'Invalid credentials'));
            }
        }
    };

    const CountrySelect = ({ field }: { field: FormField }) => (
        <div>
            <StyledFormSelectLabel for="country-select" id="country-select-label">
                {`${localize('country', 'Country')}*`}
            </StyledFormSelectLabel>

            <StyledFormSelectWrapper>
                <StyledFormSelect
                    id="country-select"
                    value={formStore.fields[ 'country' ]}
                    name='country'
                    autocomplete={field.autocomplete}
                    onChange={(e: any) => setFormStoreField({ country: e.target.value })}
                    aria-labelledby="country-select-label"
                    aria-describedby="desc-country"
                >
                    {getCountryOptions({
                        localize,
                        translations,
                        currentSelectCountryCode: formStore.fields[ 'country' ],
                        isAtosCare: siteInfo.siteType === 'atos-care'
                    })}
                </StyledFormSelect>
                <StyledIconWrapper>
                    <AngleDownIcon height={1.2} width={1.2} min-width="1.2" min-height="1.2" fill="black" ariaHidden={true} />
                </StyledIconWrapper>
            </StyledFormSelectWrapper>

            <span id="desc-country" style={{ display: 'none' }}>
                {localize('country', 'Country')}
            </span>
        </div>
    );

    const UserTypeSelect = ({ field }: { field: FormField; }) => (
        <div>
            <StyledFormSelectLabel for="user-type-select" id="user-type-select-label">
                {`${localize('user-type', 'User type')}${field.required ? '*' : ''}`}
            </StyledFormSelectLabel>

            <StyledFormSelectWrapper>
                <StyledFormSelect
                    id="user-type-select"
                    value={formStore.fields[ 'user-type' ]}
                    name='user-type'
                    autocomplete={field.autocomplete}
                    onChange={(e: any) => setFormStoreField({ 'user-type': e.target.value })}
                    aria-labelledby="user-type-select-label"
                    aria-describedby="desc-user-type"
                >
                    {getUserTypeOptions(localize, translations)}
                </StyledFormSelect>
                <StyledIconWrapper>
                    <AngleDownIcon height={1.2} width={1.2} min-width="1.2" min-height="1.2" fill="black" ariaHidden={true} />
                </StyledIconWrapper>
            </StyledFormSelectWrapper>

            <span id="desc-user-type" style={{ display: 'none' }}>
                {localize('user-type', 'User type')}
            </span>
        </div>
    );

    const CareGiverCheckbox = () => (
        <StyledCheckboxGroup>
            <Checkbox
                id="is-caregiver-checkbox"
                isChecked={formStore.fields[ 'is-caregiver' ]}
                whenClicked={() => {
                    setError('');
                    setFormStoreField({ 'is-caregiver': !formStore.fields[ 'is-caregiver' ] });
                }}
                value='is-caregiver'
                name={localize('is-caregiver-registration-question', 'Are you a caregiver?')}
                aria-describedby="desc-is-caregiver"
                aria-label={localize('is-caregiver-registration-question', 'Are you a caregiver?')}
            />

            <span id="desc-is-caregiver" style={{ display: 'none' }}>
                {localize('is-caregiver-registration-question', 'Are you a caregiver?')}
            </span>
        </StyledCheckboxGroup>
    );

    const CompanyField = () => (
        <div>
            <StyledFormInput
                id="company-field"
                value={formStore.fields[ 'company' ]}
                placeholder={getPlaceholder({ name: 'Company', slug: 'company', required: true }, localize)}
                onChange={(e) => setFormStoreField({ [ 'company' ]: e.currentTarget.value })}
                onFocus={() => setError('')}
                type='text'
                name={'company'}
                required={true}
                aria-label={localize('company', 'Company')}
                aria-describedby="desc-company"
            />

            <span id="desc-company" style={{ display: 'none' }}>
                {localize('company', 'Company')}
            </span>
        </div>
    );

    const PatientField = ({ value, field }: { value: string; field: FormField }) => (
        <div style={field.autoWidth
            ? {
                width: 'auto',
                'flex-grow': '1'
            } : {
                width: '100%'
            }}
        >
            <StyledFormInput
                id={`patient-field-${field.slug}`}
                value={value}
                placeholder={getPlaceholder(field, localize)}
                onChange={(e) => setFormStoreField({ [ field.slug ]: e.currentTarget.value })}
                onFocus={() => setError('')}
                type={field.type ? field.type : 'text'}
                name={field.slug}
                aria-label={getPlaceholder(field, localize)}
                aria-describedby={`desc-${field.slug}`}
            />

            <span id={`desc-${field.slug}`} style={{ display: 'none' }}>
                {getPlaceholder(field, localize)}
            </span>
        </div>
    );

    const PasswordField = () => (
        <div>
            <StyledFormInput
                id="password-field"
                value={formStore.fields.password}
                name='password'
                autocomplete='new-password'
                placeholder={localize('password', 'Password')}
                type="password"
                onChange={(e) => setFormStoreField({ password: e.currentTarget.value })}
                onFocus={() => setError('')}
                aria-label={localize('password', 'Password')}
                aria-describedby="desc-password"
            />

            <span id="desc-password" style={{ display: 'none' }}>
                {localize('password', 'Password')}
            </span>
        </div>
    );

    const VerifyPasswordField = () => (
        <div>
            <StyledFormInput
                id="verify-password-field"
                value={formStore.fields.verifyPassword}
                placeholder={localize('verify-password', 'Verify Password')}
                name='verify-password'
                autocomplete='new-password'
                type="password"
                onChange={(e) => setFormStoreField({ verifyPassword: e.currentTarget.value })}
                onFocus={() => setError('')}
                aria-label={localize('verify-password', 'Verify Password')}
                aria-describedby="desc-verify-password"
            />

            <span id="desc-verify-password" style={{ display: 'none' }}>
                {localize('verify-password', 'Verify Password')}
            </span>
        </div>
    );

    const ConsentCheckbox = () => (
        <StyledCheckboxGroup>
            <Checkbox
                id='privacy-policy-consent'
                isChecked={formStore.fields.consent}
                whenClicked={() => {
                    setError('');
                    setFormStoreField({ consent: !formStore.fields[ 'consent' ] });
                }}
                value='privacy-policy-consent'
                name={localize('agree-to-terms', 'I agree to the terms and conditions')}
                aria-label={localize('agree-to-terms', 'I agree to the terms and conditions')}
                aria-describedby="desc-privacy-policy-consent"
            />

            <Link noBlockSpacing={true} label={localize('read-more-here', 'Read more here')} url={privacyPolicyPage || '/'} />

            <span id="desc-privacy-policy-consent" style={{ display: 'none' }}>
                {localize('agree-to-terms', 'I agree to the terms and conditions')}
            </span>
        </StyledCheckboxGroup>
    );

    const renderSuccessMessage = () => {
        return (
            <div class="p-l tablet:p-xxxl flex flex-col items-center bg-white rounded-[var(--radius-large)]">
                <div class="mb-s w-[80px] h-[80px] tablet:w-[100px] tablet:h-[100px]">
                    {props.successIcon}
                </div>

                <div class="mb-s mt-s">
                    {props.successMessageComponents}
                </div>

                <Show when={props.isAtosCareSite && arrivedFromUrl()}>
                    <StyledFlexRow justifyContent='center'>
                        <Link
                            url={arrivedFromUrl()}
                            label={localize('registration-continue-where-you-left-off', 'Continue where you left off')}
                            plainStyle={true}
                            arrowDirection={'left'}
                            arrowPosition='left'
                            noBlockSpacing
                        />
                    </StyledFlexRow>
                </Show>

                <StyledVerticalSpace size={2} />

                {props.navigationComponents ? props.navigationComponents : null}
            </div>
        );
    };


    return (
        <ErrorCatcher componentName='Forms'>

            <Show when={success()}>
                {renderSuccessMessage()}
            </Show>

            <StyledRegistrationForm>
                <Show when={privacyPolicyPage && privacyPolicyPage !== '' }>
                    <Show when={!success()}>
                        <Heading tag="h2">{localize('user-information', 'User information')}</Heading>
                        <For each={props.fields}>
                            {(field: FormField) => {
                                if (field.slug === 'country') {
                                    return <CountrySelect field={field} />;
                                } else if (field.slug === 'user-type') {
                                    return <UserTypeSelect field={field} />;
                                } else if (field.slug === 'is-caregiver') {
                                    return <CareGiverCheckbox />;
                                } else {
                                    if (field.slug.includes('patient')) return null;

                                    if (field.slug === 'showpadCode' && !coursesListingPage.url) {
                                        return null;
                                    }

                                    const value = formStore.fields[field.slug as FormStoreKey];
                                    if (typeof value !== 'string') return null;

                                    return (
                                        <div>
                                            <StyledFormInput
                                                id={`field-${field.slug}`}
                                                value={value}
                                                placeholder={getPlaceholder(field, localize)}
                                                onChange={(e) => setFormStoreField({ [ field.slug ]: e.currentTarget.value })}
                                                onFocus={() => setError('')}
                                                type={field.type ? field.type : 'text'}
                                                name={field.slug}
                                                autocomplete={field.autocomplete}
                                                required={field.required}
                                                aria-label={getPlaceholder(field, localize).replace('*', '')}
                                                aria-describedby={`desc-${field.slug}`}
                                            />
                                            <span id={`desc-${field.slug}`} style={{ display: 'none' }}>
                                                {getPlaceholder(field, localize).replace('*', '')}
                                            </span>
                                        </div>
                                    );
                                }
                            }}
                        </For>
                        <Show when={isBusinessLead()}>
                            <CompanyField />
                        </Show>

                        <Show when={formStore.fields[ 'is-caregiver' ]}>
                            <StyledVerticalSpace size={0.5} />
                            <Heading tag="h2">
                                {localize('patient-information', 'Patient information')}
                            </Heading>
                            <StyledPatientInfo>
                                <For each={props.fields}>
                                    {(field: FormField) => {
                                        const value = formStore.fields[ field.slug as FormStoreKey ];
                                        if (typeof value !== 'string') return null;

                                        if (field.slug.includes('patient')) {
                                            return <PatientField value={value} field={field} />;
                                        }
                                    }}
                                </For>
                            </StyledPatientInfo>
                        </Show>
                        <StyledVerticalSpace size={0.5} />
                        <Heading tag="h2">{localize('password', 'Password')}</Heading>

                        <PasswordField />

                        <VerifyPasswordField />

                        <Text noBlockSpacing={true} fontSize='small' fontStyle='italic' color='darkGray'>{`*${localize('required-fields', 'required fields')}`}</Text>
                        <StyledVerticalSpace size={0.5} />

                        <ConsentCheckbox />
                    </Show>

                    <Show when={formStore.error}>
                        <StyledErrorMessage>{formStore.error}</StyledErrorMessage>
                    </Show>

                    <Show when={loading()}>
                        <StyledLoadingSpinner />
                    </Show>
                    <Show when={!success()}>
                        <Button 
                            type="submit"
                            label={localize('submit-registration-form-button-text', 'Submit')}
                            onClick={(e) => submit(e)}
                        />
                            
                        <StyledFlexColumn>
                            <Text fontSize='small' fontStyle='italic' color='darkGray' noBlockSpacing={true}>
                                {localize('recaptcha-policy-and-terms-disclaimer', 'This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.')}
                            </Text>
                            <StyledHorizontalSpace size={1} />
                            <Text fontSize='small' fontStyle='italic' color='darkGray'>
                                {`<a href="https://policies.google.com/privacy">${localize('recaptcha-privacy-policy', 'Privacy Policy')}</a> | <a href="https://policies.google.com/terms">${localize('recaptcha-terms-of-service', 'Terms of Service')}</a>`}
                            </Text>
                        </StyledFlexColumn>
                    </Show>
                </Show>
            </StyledRegistrationForm>
        </ErrorCatcher>
    );
};
